import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { fg } from '@atlaskit/platform-feature-flags';

/*
 * Remove isConvoAIKnownMessageTemplate
 * when cleaning up editor_ai_loading_text_fallback_to_generating
 */
export type ConvoAIKnownMessageTemplate =
	| 'ANALYSING_QUERY'
	| 'CONTENT_SEARCH'
	| 'PAGE_HYDRATION'
	| 'NEXT_BEST_TASK'
	| 'WRITING';

/*
 * Remove isConvoAIKnownMessageTemplate
 * when cleaning up editor_ai_loading_text_fallback_to_generating
 */
export function isConvoAIKnownMessageTemplate(type: string): type is ConvoAIKnownMessageTemplate {
	return [
		'ANALYSING_QUERY',
		'CONTENT_SEARCH',
		'PAGE_HYDRATION',
		'NEXT_BEST_TASK',
		'WRITING',
	].includes(type);
}
/**
 * @param messageTemplate The MessageDescriptor that corresponds to the loading status.
 * @returns Defaults to "Generating" if the messageTemplate is unknown or undefined.
 */
export const mapLoadingStatusToMessageTemplate = (
	messageTemplate: string | undefined,
): /**
 * When cleaning up `editor_ai_loading_text_fallback_to_generating` this will always return a MessageDescriptor
 * so remove the `| undefined` from the return type
 */
MessageDescriptor | undefined => {
	switch (messageTemplate) {
		case 'ANALYSING_QUERY':
			return aiExperienceApplicationMessages.messageTemplateAnalysingQuery;
		case 'CONTENT_SEARCH':
			return aiExperienceApplicationMessages.messageTemplateContentSearch;
		case 'PAGE_HYDRATION':
			return aiExperienceApplicationMessages.messageTemplatePageHydration;
		case 'NEXT_BEST_TASK':
			return aiExperienceApplicationMessages.messageTemplateNextBestTask;
		case 'WRITING':
			return aiExperienceApplicationMessages.loadingTitle;
		default:
			return fg('editor_ai_loading_text_fallback_to_generating')
				? aiExperienceApplicationMessages.generatingAiResponse
				: undefined;
	}
};

export const getLoadingMessage = ({
	status,
	formatMessage,
}: {
	status?: string;
	formatMessage: IntlShape['formatMessage'];
}) => {
	if (fg('editor_ai_loading_text_fallback_to_generating')) {
		const messageTemplate = mapLoadingStatusToMessageTemplate(status);
		/**
		 * When cleaning up `editor_ai_loading_text_fallback_to_generating` messageTemplate will always exist
		 * so remove this check
		 */
		if (messageTemplate) {
			return formatMessage(messageTemplate);
		} else {
			return formatMessage(aiExperienceApplicationMessages.generatingAiResponse);
		}
	} else {
		if (!status || !isConvoAIKnownMessageTemplate(status)) {
			return status;
		}

		const messageTemplate = mapLoadingStatusToMessageTemplate(status);
		if (messageTemplate) {
			return formatMessage(messageTemplate);
		}

		return status;
	}
};
